/** The pure part of a filing's standing on the home screen (gate 26).
 *
 * A filing's standing is **read from the report the Reports screen reads** (`useReport`, the same
 * query key) and described here without re-deciding anything: the verdict is the server's, its
 * reason is the server's sentence, the findings are the server's rows. What this file adds is the
 * three ways a read can leave the screen, because the dangerous one is the third:
 *
 * - `reading` - the request is in flight;
 * - `failed` - it answered with an error, and the screen says so;
 * - `ready` - a report arrived, and its verdict is one of the six states or is **not recognised**.
 *
 * **Absence never reads as success.** There is no branch here that maps "no report" to a passing
 * state, and an unrecognised verdict string is shown as unrecognised and counted as not passed,
 * rather than being matched against a default that happens to be green.
 */

import type { Finding } from "../api/queries";
import { PASSING, VERDICT_HEADLINE, VERDICT_STATES } from "./verdictWords";

/** How many blocking findings the home screen names. The rest are one link away, counted. */
export const NAMED_FINDINGS = 3;

export interface VerdictReading {
  /** The server's string, kept verbatim for `data-verdict`. */
  verdict: string;
  /** Whether it is one of the six states. */
  known: boolean;
  /** The words for it, or the honest sentence for an unrecognised one. */
  headline: string;
  /** Whether the checks ran and passed. An unrecognised verdict is never passed. */
  passed: boolean;
}

export function readVerdict(verdict: string): VerdictReading {
  const known = (VERDICT_STATES as readonly string[]).includes(verdict);
  return {
    verdict,
    known,
    headline: known
      ? (VERDICT_HEADLINE[verdict] ?? verdict)
      : `The report's state, "${verdict}", is not one this screen recognises`,
    passed: known && PASSING.has(verdict),
  };
}

export interface FindingsSummary {
  blocking: number;
  advisory: number;
  /** The first `NAMED_FINDINGS` blocking findings, in the order the server returned them. */
  named: Finding[];
  /** Blocking findings beyond the named ones. */
  further: number;
}

export function summariseFindings(findings: readonly Finding[]): FindingsSummary {
  const blocking = findings.filter((f) => f.severity === "blocking");
  return {
    blocking: blocking.length,
    advisory: findings.filter((f) => f.severity === "advisory").length,
    named: blocking.slice(0, NAMED_FINDINGS),
    further: Math.max(0, blocking.length - NAMED_FINDINGS),
  };
}
